/**
 * AppLayout - Top-level layout wrapper
 * Desktop: a fixed floating sidebar beside the page, on a unified background.
 * Below the mobile breakpoint: the same sidebar opens as an off-canvas sheet
 * from a menu button in a top bar, and the page takes the full width.
 *
 * Both widths render one tree with the page at the same place in it, so
 * crossing the breakpoint changes what surrounds the page without remounting
 * the page itself.
 */

import { Menu } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { SidebarProvider, useSidebar } from "@/components/ui/sidebar";
import { useAuthStore } from "@/features/auth/store/auth.store";
import { RoleNotActivePrompt } from "@/features/roles/components/role-not-active-prompt";
import { RoleSwitchAnnouncer } from "@/features/roles/components/role-switch-announcer";
import { RoleUnreadDot } from "@/features/roles/components/role-unread-dot";
import { NotificationsPopover } from "@/features/settings/components/notifications-popover";
import { NotificationsRealtimeBridge } from "@/features/settings/components/notifications-realtime-bridge";
import {
	StudioAssistant,
	useCommandPalette,
} from "@/features/studio/shell/assistant";
import { AppSidebar } from "./app-sidebar";

interface AppLayoutProps {
	children: React.ReactNode;
}

const PAGE_BACKGROUND =
	"radial-gradient(ellipse at 0% 0%, color-mix(in srgb, var(--color-dashboard-accent-light) 6%, transparent) 0%, transparent 45%), radial-gradient(ellipse at 100% 100%, color-mix(in srgb, var(--color-dashboard-accent) 8%, transparent) 0%, transparent 50%), var(--color-dashboard-surface)";

/**
 * Height of the mobile top bar, published to the top bar and the page as
 * `--app-header-h`. Page-level sticky elements use it as their `top`, with a
 * 0px fallback: the variable is unset on desktop, where there is no top bar.
 */
const MOBILE_HEADER_HEIGHT = "56px";

interface MobileTopBarProps {
	menuOpen: boolean;
	onOpenMenu: () => void;
}

function MobileTopBar({ menuOpen, onOpenMenu }: MobileTopBarProps) {
	return (
		<header
			className="sticky top-0 z-40 flex items-center gap-2 border-b px-3 backdrop-blur"
			style={{
				height: "var(--app-header-h)",
				background:
					"color-mix(in srgb, var(--color-dashboard-paper) 85%, transparent)",
				borderColor:
					"color-mix(in srgb, var(--color-dashboard-border) 85%, transparent)",
			}}
		>
			<button
				type="button"
				aria-label="Open navigation"
				aria-expanded={menuOpen}
				onClick={onOpenMenu}
				className="relative flex h-9 w-9 items-center justify-center rounded-full"
				style={{ color: "var(--color-dashboard-muted-dark)" }}
			>
				<Menu className="h-5 w-5" />
				<RoleUnreadDot className="right-1 top-1" />
			</button>
			<span
				style={{
					fontFamily: "var(--font-display)",
					fontWeight: 700,
					fontSize: 22,
					letterSpacing: "-0.02em",
					color: "var(--color-dashboard-text)",
				}}
			>
				finish
				<span
					style={{
						fontStyle: "italic",
						fontWeight: 500,
						color: "var(--color-dashboard-accent-dark)",
						marginLeft: -1,
					}}
				>
					d.
				</span>
			</span>
			<div className="ml-auto">
				<NotificationsPopover variant="sheet" />
			</div>
		</header>
	);
}

function LayoutContent({ children }: { children: React.ReactNode }) {
	const { state, isMobile, openMobile, setOpenMobile } = useSidebar();
	const { user } = useAuthStore();
	const { pathname } = useLocation();
	const collapsed = state === "collapsed";
	const [assistantOpen, setAssistantOpen] = useState(false);
	const isDesigner = user?.userType === "designer";

	// The menu sheet closes when the path changes, and does not reappear open
	// if the window crosses the breakpoint and comes back. A change to the
	// search or hash alone leaves it open: pages rewrite their own query
	// string with replace navigations, and one of those landing while the
	// menu is open must not shut it. Choosing a sidebar item closes the sheet
	// itself, which covers choosing the page already showing.
	// biome-ignore lint/correctness/useExhaustiveDependencies: pathname and isMobile are what trigger the close; the body does not read them
	useEffect(() => {
		setOpenMobile(false);
	}, [pathname, isMobile, setOpenMobile]);

	// The command palette is owned here, not by the sidebar, because on mobile
	// the sidebar is mounted only while the menu sheet is open. Showing the
	// palette closes the sheet: the sheet is modal, and while it is open
	// everything outside it, the palette included, takes no pointer input and
	// is aria-hidden. Only designers have a palette, so for everyone else
	// both do nothing, and Cmd+K leaves an open menu alone.
	const toggleAssistant = useCallback(() => {
		if (!isDesigner) return;
		setOpenMobile(false);
		setAssistantOpen((open) => !open);
	}, [isDesigner, setOpenMobile]);
	const openAssistant = useCallback(() => {
		if (!isDesigner) return;
		setOpenMobile(false);
		setAssistantOpen(true);
	}, [isDesigner, setOpenMobile]);
	const closeAssistant = useCallback(() => setAssistantOpen(false), []);
	useCommandPalette(toggleAssistant);

	return (
		<>
			{isMobile ? (
				<Sheet open={openMobile} onOpenChange={setOpenMobile}>
					<SheetContent
						side="left"
						aria-describedby={undefined}
						className="w-[268px] border-0 bg-transparent p-0 shadow-none sm:max-w-none [&>button]:hidden"
					>
						<SheetTitle className="sr-only">Navigation</SheetTitle>
						<AppSidebar onOpenAssistant={openAssistant} />
					</SheetContent>
				</Sheet>
			) : (
				<AppSidebar onOpenAssistant={openAssistant} />
			)}
			{!isMobile && (
				<div className="fixed right-3 top-5 z-500">
					<NotificationsPopover />
				</div>
			)}
			<div
				className={isMobile ? undefined : "group/sidebar-wrapper"}
				data-collapsible={isMobile ? undefined : collapsed ? "icon" : ""}
				style={
					isMobile
						? ({
								"--app-header-h": MOBILE_HEADER_HEIGHT,
								minHeight: "100vh",
								width: "100%",
								background: PAGE_BACKGROUND,
							} as React.CSSProperties)
						: {
								width: "100%",
								paddingLeft: collapsed ? 120 : 272,
								transition: "padding-left 420ms cubic-bezier(0.22,1,0.36,1)",
								minHeight: "100vh",
								background: PAGE_BACKGROUND,
							}
				}
			>
				{isMobile && (
					<MobileTopBar
						menuOpen={openMobile}
						onOpenMenu={() => setOpenMobile(true)}
					/>
				)}
				<main className={isMobile ? "min-w-0" : "flex-1  mr-4 mb-4"}>
					{children}
				</main>
			</div>
			{isDesigner && (
				<StudioAssistant open={assistantOpen} onClose={closeAssistant} />
			)}
			<RoleNotActivePrompt />
			<RoleSwitchAnnouncer />
		</>
	);
}

export function AppLayout({ children }: AppLayoutProps) {
	return (
		<SidebarProvider>
			<NotificationsRealtimeBridge />
			<LayoutContent>{children}</LayoutContent>
		</SidebarProvider>
	);
}
